import { pipe } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { useCollections } from './useCollections';
import { CMSGameCategory } from '@/api/cms';
import { correctLang } from '@/api/cms/base';
import { filterByGroups } from '@/helpers/cms';
import { UpdatedGameCategory } from '@/helpers/game';
import { formatCategories } from '@/helpers/gameFiltration';
import { fetcherCMS } from '@/modules/api';
import {
  CMSSortTypeEnum,
  createCMSQuery,
  getCMSCountryQuery,
  getCMSDurationQuery,
  getCMSSchedulerQuery,
  getCMSShowGuestQuery,
} from '@/modules/cms';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore } from '@/store';

export const useCMSGameCategories = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const user = useCommonStore.use.user();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const pendingUserPure = useCommonStore.use.pendingUserPure();
  const { countryCode } = useServer();

  const { collections } = useCollections();

  const query = useMemo(() => {
    if (pendingUserPure) return null;
    return createCMSQuery({
      sort: [`order:${CMSSortTypeEnum.ASC}`],
      locale: [correctLang(language)],
      filters: {
        $and: [
          ...getCMSCountryQuery(countryCode),
          getCMSSchedulerQuery(),
          getCMSDurationQuery(),
          ...getCMSShowGuestQuery(isAuthenticated),
        ],
      },
      populate: ['scheduler', 'sub_categories'],
    });
  }, [language, countryCode, isAuthenticated, pendingUserPure]);

  const {
    data = [],
    error,
    isLoading,
  } = useSWRImmutable<CMSGameCategory[]>(
    query ? `/game-categories?${query}` : null,
    fetcherCMS
  );

  const categories = useMemo(() => {
    if (!data.length || !collections.length) return [];
    return pipe<
      [CMSGameCategory[]],
      UpdatedGameCategory[],
      UpdatedGameCategory[]
    >(
      formatCategories(isAuthenticated, collections),
      // TODO: optimize logic to avoid client-side filterByGroups
      filterByGroups<UpdatedGameCategory>({ user })
    )(data);
  }, [data, user, isAuthenticated, collections]);

  return {
    data,
    isLoading,
    isError: error,
    categories,
  };
};
